import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Home, Compass, CalendarHeart, MessageCircle, User, Bell, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getSubscriptionState, enablePush, autoEnablePush } from "@/lib/push";
import VerifyReminder from "@/components/VerifyReminder";

const NAV = [
    { to: "/support", label: "Help", icon: LifeBuoy },
    { to: "/app", label: "Home", icon: Home },
    { to: "/discover", label: "Discover", icon: Compass },
    { to: "/outings", label: "My Outings", icon: CalendarHeart },
    { to: "/messages", label: "Messages", icon: MessageCircle },
    { to: "/profile", label: "Profile", icon: User },
];

function NotificationBell() {
    const qc = useQueryClient();
    const nav = useNavigate();
    const { data } = useQuery({
        queryKey: ["notifications"],
        queryFn: async () => (await api.get("/me/notifications")).data,
        refetchInterval: 20000,
    });
    const unread = data?.unread || 0;
    const notes = data?.notifications || [];

    const open = async (n) => {
        await api.post(`/me/notifications/${n.id}/read`);
        qc.invalidateQueries({ queryKey: ["notifications"] });
        if (n.outing_id) nav(`/outings/${n.outing_id}${n.type === "feedback_request" ? "?tab=feedback" : ""}`);
        else if (n.group_id) nav(`/discover?group=${n.group_id}`);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notif-bell">
                    <Bell className="h-5 w-5 text-primary" />
                    {unread > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-700 text-white">{unread}</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <p className="font-600 text-primary">Notifications</p>
                    {unread > 0 && (
                        <button className="text-xs text-accent hover:underline" data-testid="notif-mark-all"
                            onClick={async () => { await api.post("/me/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); }}>
                            Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-80 overflow-auto">
                    {notes.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
                    {notes.map((n) => (
                        <button key={n.id} data-testid="notif-item" onClick={() => open(n)}
                            className={`block w-full border-b px-4 py-3 text-left hover:bg-secondary/50 ${!n.read ? "bg-secondary/30" : ""}`}>
                            <p className="text-sm font-600 text-primary">{n.title}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground">{n.body}</p>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function MemberLayout() {
    // Light/dark theme is only available inside the member experience; public site stays dark.
    useEffect(() => {
        let light = false;
        try { light = localStorage.getItem("ssc_theme") === "light"; } catch (e) {}
        document.documentElement.classList.toggle("light", light);
        return () => document.documentElement.classList.remove("light");
    }, []);

    // Connect push once a member is in the app: silently re-subscribe if already
    // granted, otherwise nudge them once per session to turn notifications on.
    useEffect(() => {
        (async () => {
            if (!pushSupported() || Notification.permission === "denied") return;
            if (Notification.permission === "granted") { autoEnablePush(); return; }
            if (sessionStorage.getItem("ssc_push_nudge")) return;
            if ((await getSubscriptionState()) === "subscribed") return;
            sessionStorage.setItem("ssc_push_nudge", "1");
            toast("Turn on notifications", {
                description: "Get outing invites, venue picks, and reminders the moment they happen.",
                duration: 12000,
                action: {
                    label: "Enable",
                    onClick: () => enablePush()
                        .then(() => toast.success("Notifications are on for this device."))
                        .catch((e) => toast.error(e.message || "Couldn't enable notifications.")),
                },
            });
        })();
    }, []);

    return (
        <div className="min-h-screen bg-background pb-24">
            <VerifyReminder />
            <header className="sticky top-0 z-40 border-b border-black/5 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-3">
                    <Logo />
                    <div className="flex items-center gap-1">
                        <ThemeToggle />
                        <NotificationBell />
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-2xl px-5 pt-6 pb-28">
                <Outlet />
            </main>
            <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-background/85 backdrop-blur-xl">
                <div className="mx-auto flex max-w-2xl items-center justify-around px-2 py-2" style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}>
                    {NAV.map((n) => (
                        <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center gap-1 rounded-full px-3.5 py-2 text-[10px] font-600 transition-all active:scale-90 ${
                                    isActive ? "bg-accent text-accent-foreground shadow-md" : "text-muted-foreground hover:bg-white/5 hover:text-primary"
                                }`}>
                            {({ isActive }) => (<><motion.span animate={isActive ? { scale: [1, 1.35, 1] } : { scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 12 }}><n.icon className="h-5 w-5" /></motion.span><span>{n.label}</span></>)}
                        </NavLink>
                    ))}
                </div>
            </nav>
        </div>
    );
}
